import { useEffect, useMemo, useRef, useState } from "react";

import { message, Form } from "@dreambigger/design-system";
import {
  CharitableOrganizationSearchStepAssets,
  ResponseFields,
} from "@dreambigger/shared/src/types";
import { CharitableOrganizationDto } from "@dreambigger/shared/src/types/charitable-organization";
import { useSegment } from "@dreambigger/shared/src/hooks";

import StepWrapper from "./step-wrapper";
import CharitableOrganizationSearch from "../components/CharitableOrganizationSearch/CharitableOrganizationSearch";
import { useApplication } from "../api";
import { StepProps } from "../pages/flows/[flowId]";
import { useAuthToken } from "../passwordless-auth";
import { changeStepForward } from "../utils/changeStepForward";
import { prefixFieldsWithSlug } from "../utils/prefixStepSlug";

/**
 * Charitable organization search step: the donor searches the org-search
 * API (submit-driven) and selects one organization. On NEXT, the selected
 * org is saved under the step slug using the same 7 field keys the legacy
 * prefill flow writes — so downstream consumers (payment recipient, exports,
 * emails) are source-agnostic.
 */
export default function StepCharitableOrganizationSearch({
  flow,
  step,
  brand,
  progress,
}: StepProps) {
  const {
    noResultsText,
    resultsPerPage,
    causeOptions,
    nextSlug,
  }: CharitableOrganizationSearchStepAssets = step.assets;
  const { slug, type, assets } = step;

  const tokenHelper = useAuthToken(flow.financialInstitution.id);
  const segment = useSegment();
  const applicationHelper = useApplication(flow.financialInstitution.id);

  const [form] = Form.useForm();
  const [selectedOrg, setSelectedOrg] =
    useState<CharitableOrganizationDto | null>(null);
  const [processing, setProcessing] = useState(false);

  const application = useMemo(
    () => applicationHelper.find(flow.id, "draft"),
    [applicationHelper, flow]
  );

  const previousResponse = useMemo(() => {
    return applicationHelper.findResponse(flow.id, "draft", slug);
  }, [applicationHelper, flow, slug]);

  // Rehydrate a previously saved selection (returning donor). Runs at most
  // once — if previousResponse later changes identity (e.g. the application
  // helper re-renders), we must not re-select an org the user has since
  // deselected via "Change selection".
  const didRehydrate = useRef(false);
  useEffect(() => {
    const fields = previousResponse?.fields;
    if (didRehydrate.current || !fields || !fields.organizationThriventId) {
      return;
    }
    didRehydrate.current = true;
    setSelectedOrg({
      organizationThriventId: fields.organizationThriventId as string,
      organizationEin: (fields.organizationEin as string) ?? null,
      organizationName: (fields.organizationName as string) ?? "",
      organizationAddress: (fields.organizationAddress as string) ?? null,
      organizationCity: (fields.organizationCity as string) ?? null,
      organizationState: (fields.organizationState as string) ?? null,
      organizationZip: (fields.organizationZip as string) ?? null,
      purpose: null,
      webUrl: null,
      typeDescription: null,
      focuses: [],
    });
  }, [previousResponse]);

  const handleNext = () => {
    if (!application) {
      message.error("Unable to find application");
      return;
    }
    if (!selectedOrg) {
      message.error("Please select an organization.");
      return;
    }

    setProcessing(true);

    // The 7 downstream contract keys, saved under this step's slug. Id and
    // EIN are stringified — the API may return them as numbers, and an EIN
    // stored as a number would drop leading zeros downstream.
    const saveData: ResponseFields = {
      organizationThriventId:
        selectedOrg.organizationThriventId != null
          ? String(selectedOrg.organizationThriventId)
          : "",
      organizationEin:
        selectedOrg.organizationEin != null
          ? String(selectedOrg.organizationEin)
          : "",
      organizationName: selectedOrg.organizationName,
      organizationAddress: selectedOrg.organizationAddress ?? "",
      organizationCity: selectedOrg.organizationCity ?? "",
      organizationState: selectedOrg.organizationState ?? "",
      organizationZip: selectedOrg.organizationZip ?? "",
    };

    applicationHelper
      .update(application.id, {
        slug,
        type,
        fields: saveData,
      })
      .then((saved) => {
        // Don't move forward if the response is not saved.
        if (!saved) {
          return;
        }

        if (assets.runTransitionRules) {
          return applicationHelper
            .getNextStep({
              applicationId: application.id,
              stepSlug: slug,
              transformUUID:
                typeof assets.runTransitionRules === "object"
                  ? assets.runTransitionRules.transformUUID
                  : undefined,
            })
            .then(({ data }) => {
              if (data.nextStep) {
                changeStepForward(data.nextStep);
              } else {
                changeStepForward(nextSlug, saveData);
              }
            });
        } else {
          changeStepForward(nextSlug, saveData);
        }
      })
      .finally(() => {
        setProcessing(false);

        segment.track({
          action: "Button Click",
          label: `Submit - ${slug}`,
          properties: prefixFieldsWithSlug({ fields: saveData, slug }),
        });
      });
  };

  const wrapperProps = {
    step,
    brand,
    flow,
    progress,
    form,
    disabled: !selectedOrg,
    processing,
    handleNext,
  };

  return (
    <StepWrapper {...wrapperProps}>
      <CharitableOrganizationSearch
        flowId={flow.id}
        tokenHelper={tokenHelper}
        brand={brand}
        noResultsText={noResultsText}
        resultsPerPage={resultsPerPage}
        causeOptions={causeOptions}
        selectedOrg={selectedOrg}
        onSelect={setSelectedOrg}
      />
    </StepWrapper>
  );
}
